import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Job, EvaluationCriteria, EvaluationCriteriaObject, SalaryRange, PipelineConfig } from '@recruitai/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MessageSquare } from 'lucide-react';
import { PipelineConfigurator } from '@/components/pipeline/PipelineConfigurator';

interface JobFormProps {
  job?: Job;
  onSubmit: (data: JobFormData) => Promise<void>;
  isLoading?: boolean;
  initialJobData?: { title?: string; description?: string; requirements?: string[]; niceToHave?: string[]; evaluationCriteria?: Array<{ name: string; description: string; weight: number }> };
  onContinueWithAI?: (formData: JobFormData) => void;
}

export interface JobFormData {
  title: string;
  description: string;
  requirements: string[];
  niceToHave: string[];
  evaluationCriteria: EvaluationCriteria;
  openedAt?: string;
  expiresAt?: string;
  allowPublicApplications?: boolean;
  publicDescription?: string;
  location?: string;
  contractType?: 'CLT' | 'PJ' | 'Estágio' | 'Freelancer';
  salaryRange?: SalaryRange;
  pipelineConfig?: PipelineConfig;
}

export function JobForm({ job, onSubmit, isLoading, initialJobData, onContinueWithAI }: JobFormProps) {
  const navigate = useNavigate();
  const [title, setTitle] = useState(job?.title || initialJobData?.title || '');
  const [description, setDescription] = useState(job?.description || initialJobData?.description || '');
  const [requirements, setRequirements] = useState(
    job?.requirements.join('\n') || initialJobData?.requirements?.join('\n') || ''
  );
  const [niceToHave, setNiceToHave] = useState(
    job?.niceToHave.join('\n') || initialJobData?.niceToHave?.join('\n') || ''
  );
  
  // Format date for display (DD/MM/YYYY) — parse ISO string directly to avoid timezone offset
  const formatDateForDisplay = (isoDate?: string) => {
    if (!isoDate) return '';
    const match = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return '';
    const [, year, month, day] = match;
    return `${day}/${month}/${year}`;
  };
  
  // Parse DD/MM/YYYY to ISO date (noon UTC to avoid timezone offset issues)
  const parseDateFromDisplay = (displayDate: string): string | undefined => {
    if (!displayDate || displayDate.length !== 10) return undefined;
    const [day, month, year] = displayDate.split('/');
    if (!day || !month || !year) return undefined;
    const d = parseInt(day), m = parseInt(month), y = parseInt(year);
    if (isNaN(d) || isNaN(m) || isNaN(y)) return undefined;
    if (d < 1 || d > 31 || m < 1 || m > 12 || y < 2000) return undefined;
    return `${y}-${month}-${day}T12:00:00Z`;
  };
  
  const [openedAt, setOpenedAt] = useState(formatDateForDisplay(job?.openedAt));
  const [expiresAt, setExpiresAt] = useState(formatDateForDisplay(job?.expiresAt));

  // External publication fields
  const [allowPublicApplications, setAllowPublicApplications] = useState(job?.allowPublicApplications ?? true);
  const [location, setLocation] = useState(job?.location || '');
  const [contractType, setContractType] = useState<string>(job?.contractType || '');
  const [showSalary, setShowSalary] = useState(!!(job?.salaryRange));
  const [salaryMin, setSalaryMin] = useState(job?.salaryRange?.min?.toString() || '');
  const [salaryMax, setSalaryMax] = useState(job?.salaryRange?.max?.toString() || '');
  const [publicDescription, setPublicDescription] = useState(job?.publicDescription || '');
  const [pipelineConfig, setPipelineConfig] = useState<PipelineConfig | undefined>(job?.pipelineConfig);
  
  // Handle date input with mask
  const handleDateInput = (value: string, setter: (value: string) => void) => {
    // Remove non-numeric characters
    const numbers = value.replace(/\D/g, '');
    
    // Apply mask DD/MM/YYYY
    let formatted = '';
    if (numbers.length > 0) {
      formatted = numbers.substring(0, 2);
      if (numbers.length >= 3) {
        formatted += '/' + numbers.substring(2, 4);
      }
      if (numbers.length >= 5) {
        formatted += '/' + numbers.substring(4, 8);
      }
    }
    
    setter(formatted);
  };
  
  // Convert EvaluationCriteria to string format for editing
  const criteriaToString = (criteria: EvaluationCriteria) => {
    if (Array.isArray(criteria)) {
      return criteria.map(c => `${c.name}:${c.weight}`).join('\n');
    }
    return `Experiência Técnica:${criteria.technical}\nExperiência:${criteria.experience}\nCultura:${criteria.culture}\nComunicação:${criteria.communication}`;
  };
  
  const [criteria, setCriteria] = useState(() => {
    if (job?.evaluationCriteria) return criteriaToString(job.evaluationCriteria);
    if (initialJobData?.evaluationCriteria && initialJobData.evaluationCriteria.length > 0) {
      return initialJobData.evaluationCriteria.map(c => `${c.name}:${c.weight}`).join('\n');
    }
    return 'Experiência Técnica:0.25\nExperiência:0.25\nCultura:0.25\nComunicação:0.25';
  });

  const getCurrentFormData = (): JobFormData => {
    const requirementsList = requirements.split('\n').map(r => r.trim()).filter(r => r.length > 0);
    const niceToHaveList = niceToHave.split('\n').map(r => r.trim()).filter(r => r.length > 0);
    const criteriaLines = criteria.split('\n').map(line => {
      const [name, weight] = line.split(':');
      return { name: name?.trim().toLowerCase() || '', weight: parseFloat(weight?.trim() || '0') };
    }).filter(c => c.name && !isNaN(c.weight));

    const evaluationCriteria: EvaluationCriteriaObject = { technical: 0.25, experience: 0.25, culture: 0.25, communication: 0.25 };
    criteriaLines.forEach(c => {
      const name = c.name.toLowerCase();
      if (name.includes('técnica') || name.includes('tecnica') || name.includes('technical')) evaluationCriteria.technical = c.weight;
      else if (name.includes('experiência') || name.includes('experiencia') || name.includes('experience')) evaluationCriteria.experience = c.weight;
      else if (name.includes('cultura') || name.includes('culture') || name.includes('cultural')) evaluationCriteria.culture = c.weight;
      else if (name.includes('comunicação') || name.includes('comunicacao') || name.includes('communication')) evaluationCriteria.communication = c.weight;
    });

    return {
      title, description,
      requirements: requirementsList,
      niceToHave: niceToHaveList,
      evaluationCriteria,
      openedAt: parseDateFromDisplay(openedAt),
      expiresAt: parseDateFromDisplay(expiresAt),
      allowPublicApplications,
      publicDescription: publicDescription || undefined,
      location: location || undefined,
      contractType: (contractType as JobFormData['contractType']) || undefined,
      salaryRange: showSalary && salaryMin && salaryMax ? { min: Number(salaryMin), max: Number(salaryMax), currency: 'BRL', showOnCareerPage: true } : undefined,
      pipelineConfig,
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate date temporal coherence
    const parsedOpenedAt = parseDateFromDisplay(openedAt);
    const parsedExpiresAt = parseDateFromDisplay(expiresAt);
    
    if (!openedAt || openedAt.length !== 10 || !parsedOpenedAt) {
      alert('Preencha a data de abertura no formato DD/MM/AAAA.');
      return;
    }
    if (!expiresAt || expiresAt.length !== 10 || !parsedExpiresAt) {
      alert('Preencha a data de validade no formato DD/MM/AAAA.');
      return;
    }
    if (new Date(parsedExpiresAt) <= new Date(parsedOpenedAt)) {
      alert('A data de validade deve ser posterior à data de abertura.');
      return;
    }

    // Parse requirements
    const requirementsList = requirements
      .split('\n')
      .map(r => r.trim())
      .filter(r => r.length > 0);

    // Parse nice to have
    const niceToHaveList = niceToHave
      .split('\n')
      .map(r => r.trim())
      .filter(r => r.length > 0);

    // Parse evaluation criteria and convert to backend format
    const criteriaLines = criteria
      .split('\n')
      .map(line => {
        const [name, weight] = line.split(':');
        return {
          name: name.trim().toLowerCase(),
          weight: parseFloat(weight.trim()),
        };
      })
      .filter(c => c.name && !isNaN(c.weight));

    // Map to backend format (technical, experience, culture, communication)
    const evaluationCriteria: EvaluationCriteriaObject = {
      technical: 0.25,
      experience: 0.25,
      culture: 0.25,
      communication: 0.25,
    };

    criteriaLines.forEach(c => {
      const name = c.name.toLowerCase();
      if (name.includes('técnica') || name.includes('tecnica') || name.includes('technical')) {
        evaluationCriteria.technical = c.weight;
      } else if (name.includes('experiência') || name.includes('experiencia') || name.includes('experience')) {
        evaluationCriteria.experience = c.weight;
      } else if (name.includes('cultura') || name.includes('culture') || name.includes('cultural')) {
        evaluationCriteria.culture = c.weight;
      } else if (name.includes('comunicação') || name.includes('comunicacao') || name.includes('communication')) {
        evaluationCriteria.communication = c.weight;
      }
    });

    // Validate salary range if shown
    if (showSalary && salaryMin && salaryMax) {
      if (Number(salaryMin) > Number(salaryMax)) {
        alert('O salário mínimo deve ser menor ou igual ao máximo.');
        return;
      }
    }

    await onSubmit({
      title,
      description,
      requirements: requirementsList,
      niceToHave: niceToHaveList,
      evaluationCriteria,
      openedAt: parseDateFromDisplay(openedAt),
      expiresAt: parseDateFromDisplay(expiresAt),
      allowPublicApplications,
      publicDescription: publicDescription || undefined,
      location: location || undefined,
      contractType: (contractType as JobFormData['contractType']) || undefined,
      salaryRange: showSalary && salaryMin && salaryMax ? { min: Number(salaryMin), max: Number(salaryMax), currency: 'BRL', showOnCareerPage: true } : undefined,
      pipelineConfig,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Informações Básicas</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="title">Título da Vaga *</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex: Desenvolvedor Full Stack Senior"
              required
            />
          </div>

          <div>
            <Label htmlFor="description">Descrição *</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Descreva a vaga, responsabilidades e o que você procura..."
              rows={6}
              required
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Datas</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="openedAt">Data de Abertura *</Label>
              <Input
                id="openedAt"
                type="text"
                placeholder="DD/MM/AAAA"
                value={openedAt}
                onChange={(e) => handleDateInput(e.target.value, setOpenedAt)}
                maxLength={10}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Campo obrigatório (DD/MM/AAAA)
              </p>
            </div>

            <div>
              <Label htmlFor="expiresAt">Data de Validade *</Label>
              <Input
                id="expiresAt"
                type="text"
                placeholder="DD/MM/AAAA"
                value={expiresAt}
                onChange={(e) => handleDateInput(e.target.value, setExpiresAt)}
                maxLength={10}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Campo obrigatório — a vaga será fechada automaticamente nesta data
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Requisitos</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="requirements">
              Requisitos Obrigatórios * (um por linha)
            </Label>
            <Textarea
              id="requirements"
              value={requirements}
              onChange={(e) => setRequirements(e.target.value)}
              placeholder="React&#10;Node.js&#10;TypeScript&#10;5+ anos de experiência"
              rows={6}
              required
            />
            <p className="text-sm text-muted-foreground mt-1">
              Digite um requisito por linha
            </p>
          </div>

          <div>
            <Label htmlFor="niceToHave">
              Requisitos Desejáveis (um por linha)
            </Label>
            <Textarea
              id="niceToHave"
              value={niceToHave}
              onChange={(e) => setNiceToHave(e.target.value)}
              placeholder="GraphQL&#10;AWS&#10;Docker&#10;Experiência com microserviços"
              rows={4}
            />
            <p className="text-sm text-muted-foreground mt-1">
              Digite um requisito desejável por linha (opcional)
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Critérios de Avaliação</CardTitle>
        </CardHeader>
        <CardContent>
          <div>
            <Label htmlFor="criteria">
              Critérios e Pesos * (formato: Nome:Peso)
            </Label>
            <Textarea
              id="criteria"
              value={criteria}
              onChange={(e) => setCriteria(e.target.value)}
              placeholder="Experiência Técnica:0.25&#10;Experiência:0.25&#10;Cultura:0.25&#10;Comunicação:0.25"
              rows={6}
              required
            />
            <p className="text-sm text-muted-foreground mt-1">
              Formato: Nome do Critério:Peso (ex: Experiência Técnica:0.25)
              <br />
              Critérios aceitos: Técnica, Experiência, Cultura, Comunicação
              <br />
              Os pesos devem somar 1.0
            </p>
          </div>
        </CardContent>
      </Card>

      {/* External Publication */}
      <Card>
        <CardHeader>
          <CardTitle>Publicação Externa</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <button
              type="button"
              role="switch"
              aria-checked={allowPublicApplications}
              onClick={() => setAllowPublicApplications(!allowPublicApplications)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${allowPublicApplications ? 'bg-green-500' : 'bg-gray-300'} cursor-pointer`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${allowPublicApplications ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
            <span className="text-sm">Permitir candidaturas pela página de carreiras</span>
          </div>

          <div>
            <Label htmlFor="location">Localização</Label>
            <Input id="location" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Ex: São Paulo, SP — Remoto" />
          </div>

          <div>
            <Label htmlFor="contractType">Tipo de Contrato</Label>
            <select id="contractType" value={contractType} onChange={(e) => setContractType(e.target.value)} className="w-full px-3 py-2 border rounded-md">
              <option value="">Não especificado</option>
              <option value="CLT">CLT</option>
              <option value="PJ">PJ</option>
              <option value="Estágio">Estágio</option>
              <option value="Freelancer">Freelancer</option>
            </select>
          </div>

          <div>
            <div className="flex items-center gap-3 mb-2">
              <button
                type="button"
                role="switch"
                aria-checked={showSalary}
                onClick={() => setShowSalary(!showSalary)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${showSalary ? 'bg-green-500' : 'bg-gray-300'} cursor-pointer`}
              >
                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${showSalary ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
              <span className="text-sm">Exibir faixa salarial</span>
            </div>
            {showSalary && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="salaryMin">Mínimo (R$)</Label>
                  <Input id="salaryMin" type="number" min="0" value={salaryMin} onChange={(e) => setSalaryMin(e.target.value)} placeholder="3000" />
                </div>
                <div>
                  <Label htmlFor="salaryMax">Máximo (R$)</Label>
                  <Input id="salaryMax" type="number" min="0" value={salaryMax} onChange={(e) => setSalaryMax(e.target.value)} placeholder="8000" />
                </div>
              </div>
            )}
          </div>

          <div>
            <Label htmlFor="publicDescription">Descrição Pública (opcional)</Label>
            <Textarea id="publicDescription" value={publicDescription} onChange={(e) => setPublicDescription(e.target.value)} placeholder="Descrição exibida na página de carreiras. Se vazio, a descrição interna será usada." rows={4} />
            <p className="text-xs text-muted-foreground mt-1">Se vazio, a descrição interna será usada na página pública</p>
          </div>
        </CardContent>
      </Card>

      {/* Pipeline Configurator */}
      <PipelineConfigurator
        initialConfig={job?.pipelineConfig}
        onConfigChange={setPipelineConfig}
      />

      <div className="flex gap-2 justify-end">
        {onContinueWithAI && (
          <Button
            type="button"
            variant="outline"
            onClick={() => onContinueWithAI(getCurrentFormData())}
            disabled={isLoading}
          >
            <MessageSquare className="h-4 w-4 mr-2" />
            Continuar com IA
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate('/jobs')}
          disabled={isLoading}
        >
          Cancelar
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? 'Salvando...' : job ? 'Atualizar Vaga' : 'Criar Vaga'}
        </Button>
      </div>
    </form>
  );
}
